// src/components/editor/EnhancedCanvas.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Maximize, Minimize, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import PriceText from './PriceText';

const EnhancedCanvas = ({ template, onUpdateTemplate, selectedElement, onSelectElement, canvasSize }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [fitMode, setFitMode] = useState('fit');
  const [customScale, setCustomScale] = useState(100);

  // Calculer l'échelle avec gestion de la stabilité et mesure en temps réel
  const calculateScale = useCallback(() => {
    if (!containerRef.current) return 1;

    const container = containerRef.current;
    
    // Utiliser directement les dimensions du conteneur en temps réel
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    // Réserver de l'espace pour le padding (40px total)
    const padding = 40;
    const availableWidth = containerWidth - padding;
    const availableHeight = containerHeight - padding;

    let newScale = 1;

    switch (fitMode) {
      case 'fit':
        // Ajuster pour que tout soit visible avec un padding
        newScale = Math.min(
          availableWidth / canvasSize.width,
          availableHeight / canvasSize.height,
          1 // Ne pas agrandir au-delà de 100%
        );
        // S'assurer qu'on a au moins 10% de la taille originale pour la lisibilité
        newScale = Math.max(newScale, 0.1);
        break;
      case 'fill':
        // Remplir l'espace disponible (peut cropper)
        newScale = Math.max(
          availableWidth / canvasSize.width,
          availableHeight / canvasSize.height
        );
        break;
      case 'actual':
        // Taille réelle
        newScale = 1;
        break;
      case 'custom':
        // Échelle personnalisée
        newScale = customScale / 100;
        break;
      default:
        newScale = 1;
    }

    return Math.max(0.1, Math.min(5, newScale)); // Limiter entre 10% et 500%
  }, [canvasSize, fitMode, customScale]);

  // Mettre à jour l'échelle avec debouncing pour éviter les saccades
  useEffect(() => {
    const updateScale = () => {
      const newScale = calculateScale();
      setScale(newScale);
    };

    // Délai pour éviter les calculs trop fréquents
    const timeoutId = setTimeout(updateScale, 100);
    return () => clearTimeout(timeoutId);
  }, [calculateScale]);

  // Recalculer lors du redimensionnement avec throttling
  useEffect(() => {
    let timeoutId;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newScale = calculateScale();
        setScale(newScale);
      }, 150); // Throttling pour la performance
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [calculateScale]);

  const handleMouseDown = useCallback((e, element) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.target.classList.contains('resize-handle')) {
      setIsResizing(element.id);
      return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    const canvasRect = canvasRef.current.getBoundingClientRect();
    
    // Calculer l'offset en tenant compte de l'échelle et de la position du canvas
    const offsetX = (e.clientX - rect.left) / scale;
    const offsetY = (e.clientY - rect.top) / scale;
    
    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(element.id);
    onSelectElement(element);
  }, [onSelectElement, scale]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging && !isResizing) return;
    
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) return;
    
    if (isDragging) {
      // Calculer la nouvelle position en tenant compte de l'échelle
      const x = (e.clientX - canvasRect.left) / scale - dragOffset.x;
      const y = (e.clientY - canvasRect.top) / scale - dragOffset.y;
      
      // Contraintes pour garder l'élément dans le canvas (coordonnées réelles)
      const constrainedX = Math.max(0, Math.min(x, canvasSize.width - 50));
      const constrainedY = Math.max(0, Math.min(y, canvasSize.height - 20));
      
      onUpdateTemplate(prev => ({
        ...prev,
        elements: prev.elements.map(el => 
          el.id === isDragging 
            ? { ...el, x: constrainedX, y: constrainedY }
            : el
        )
      }));
    } else if (isResizing) {
      // Calculer la nouvelle taille en tenant compte de l'échelle
      const mouseX = (e.clientX - canvasRect.left) / scale;
      const mouseY = (e.clientY - canvasRect.top) / scale;
      
      onUpdateTemplate(prev => ({
        ...prev,
        elements: prev.elements.map(el => {
          if (el.id === isResizing && (el.type === 'image' || el.type === 'video')) {
            const newWidth = Math.max(50, mouseX - el.x);
            const newHeight = Math.max(50, mouseY - el.y);
            
            return { 
              ...el, 
              width: Math.min(newWidth, canvasSize.width - el.x),
              height: Math.min(newHeight, canvasSize.height - el.y)
            };
          }
          return el;
        })
      }));
    }
  }, [isDragging, isResizing, dragOffset, canvasSize, onUpdateTemplate, scale]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  const renderElement = (element) => {
    const elementStyle = {
      position: 'absolute',
      left: `${element.x}px`,
      top: `${element.y}px`,
      zIndex: element.zIndex || 1,
      cursor: 'move'
    };

    const isSelected = selectedElement?.id === element.id;
    const selectionStyle = isSelected ? {
      outline: `2px solid #3b82f6`,
      outlineOffset: '2px'
    } : {};

    switch (element.type) {
       case 'text':
        return (
          <div
            key={element.id}
            style={{
              ...elementStyle,
              backgroundColor: element.backgroundColor === 'transparent' ? 'transparent' : (element.backgroundColor || 'transparent'),
              padding: element.backgroundColor && element.backgroundColor !== 'transparent' ? '4px 8px' : '0',
              borderRadius: element.backgroundColor && element.backgroundColor !== 'transparent' ? '4px' : '0',
              maxWidth: `${canvasSize.width - element.x - 10}px`,
              wordWrap: 'break-word',
              userSelect: 'none',
              ...selectionStyle
            }}
            onMouseDown={(e) => handleMouseDown(e, element)}
          >
            <PriceText
              content={element.content}
              fontSize={element.fontSize}
              color={element.color}
              fontFamily={element.fontFamily}
              fontWeight={element.fontWeight}
              fontStyle={element.fontStyle}
              enablePriceFormat={element.enablePriceFormat}
              priceFormat={element.priceFormat}
            />
            
            {/* Badge pour éléments dynamiques */}
            {element.isDynamic && element.dynamicTag && (
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">🔄</span>
              </div>
            )}
            
            {/* Badge pour format prix activé */}
            {element.enablePriceFormat && (
              <div className="absolute -top-2 -left-2 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">💰</span>
              </div>
            )}
          </div>
        );

      case 'image':
        return (
          <div
            key={element.id}
            style={{
              ...elementStyle,
              width: `${element.width}px`,
              height: `${element.height}px`,
              ...selectionStyle
            }}
            onMouseDown={(e) => handleMouseDown(e, element)}
          >
            <img
              src={element.src}
              alt={element.alt}
              style={{
                width: '100%',
                height: '100%',
                objectFit: element.objectFit || 'cover',
                pointerEvents: 'none',
                borderRadius: '4px',
                display: 'block'
              }}
              draggable={false}
              onError={(e) => {
                console.error(`Erreur de chargement pour l'image ${element.id}`);
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
              onLoad={(e) => {
                // Assurer que l'image placeholder est cachée si l'image charge
                const placeholder = e.target.nextSibling;
                if (placeholder) {
                  placeholder.style.display = 'none';
                }
              }}
            />
            
            {/* Placeholder si l'image ne charge pas */}
            <div 
              className="w-full h-full bg-gray-200 border-2 border-dashed border-gray-400 flex items-center justify-center text-gray-500 text-sm"
              style={{ display: 'none' }}
            >
              <div className="text-center">
                <div className="text-2xl mb-2">🖼️</div>
                <div>Image non trouvée</div>
                <div className="text-xs mt-1">{element.src}</div>
              </div>
            </div>
            
            {/* Poignées de redimensionnement */}
            {isSelected && (
              <>
                <div
                  className="resize-handle absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-se-resize"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setIsResizing(element.id);
                  }}
                />
                <div
                  className="resize-handle absolute top-1/2 -right-1 w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-e-resize transform -translate-y-1/2"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setIsResizing(element.id);
                  }}
                />
                <div
                  className="resize-handle absolute -bottom-1 left-1/2 w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-s-resize transform -translate-x-1/2"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setIsResizing(element.id);
                  }}
                />
              </>
            )}
          </div>
        );

      case 'video':
        return (
          <div
            key={element.id}
            style={{
              ...elementStyle,
              width: `${element.width}px`,
              height: `${element.height}px`,
              ...selectionStyle
            }}
            onMouseDown={(e) => handleMouseDown(e, element)}
          >
            <video
              src={element.src}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none',
                borderRadius: '4px'
              }}
              autoPlay={element.autoplay}
              loop={element.loop}
              muted={element.muted}
            />
            
            {/* Poignées de redimensionnement similaires aux images */}
            {isSelected && (
              <div
                className="resize-handle absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-se-resize"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setIsResizing(element.id);
                }}
              />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-100 overflow-hidden" style={{ height: '100%' }}>
      {/* Contrôles d'échelle */}
      <div className="p-4 bg-white border-b flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium">Affichage:</span>
            <select
              value={fitMode}
              onChange={(e) => setFitMode(e.target.value)}
              className="px-3 py-1 border rounded text-sm bg-white"
            >
              <option value="fit">Ajuster</option>
              <option value="fill">Remplir</option>
              <option value="actual">Taille réelle</option>
              <option value="custom">Personnalisé</option>
            </select>
          </div>

          {fitMode === 'custom' && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCustomScale(Math.max(10, customScale - 10))}
                className="p-1 hover:bg-gray-100 rounded"
                title="Zoom arrière"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <input
                type="number"
                value={Math.round(customScale)}
                onChange={(e) => setCustomScale(Math.max(10, Math.min(500, parseInt(e.target.value) || 100)))}
                className="w-16 px-2 py-1 border rounded text-sm text-center"
                min="10"
                max="500"
              />
              <span className="text-sm">%</span>
              <button
                onClick={() => setCustomScale(Math.min(500, customScale + 10))}
                className="p-1 hover:bg-gray-100 rounded"
                title="Zoom avant"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="text-sm text-gray-600">
            {canvasSize.width} × {canvasSize.height}px ({Math.round(scale * 100)}%)
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setFitMode('fit')}
            className="flex items-center space-x-1 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
          >
            <Maximize className="h-4 w-4" />
            <span>Ajuster</span>
          </button>
          <button
            onClick={() => setFitMode('actual')}
            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
          >
            100%
          </button>
        </div>
      </div>

      {/* Zone de canvas avec hauteur dynamique */}
      <div 
        ref={containerRef}
        className="flex-1 bg-gray-100"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '0',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <div
          ref={canvasRef}
          style={{
            width: `${canvasSize.width}px`,
            height: `${canvasSize.height}px`,
            background: template?.background?.type === 'gradient' 
              ? template.background.value 
              : template?.background?.type === 'color'
              ? template.background.value
              : template?.background?.type === 'image'
              ? `url(${template.background.value}) center/cover`
              : 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            position: 'relative',
            overflow: 'hidden',
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
            border: '1px solid rgba(255,255,255,0.1)',
            flexShrink: 0,
            margin: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onSelectElement(null);
            }
          }}
        >
          {/* Éléments triés par z-index */}
          {template?.elements
            ?.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
            ?.map(renderElement)
          }

          {/* Grille d'aide optionnelle (seulement si assez zoomé) */}
          {selectedElement && scale > 0.3 && (
            <div 
              className="absolute inset-0 pointer-events-none opacity-20"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                `,
                backgroundSize: `${20}px ${20}px`
              }}
            />
          )}

          {/* Indicateur de résolution dans le coin */}
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
            {canvasSize.width}×{canvasSize.height}
          </div>
        </div>
      </div>

      {/* Informations sur l'élément sélectionné */}
      {selectedElement && (
        <div className="p-4 bg-white border-t flex-shrink-0">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <span className="font-medium">
                {selectedElement.type === 'text' ? 'Texte' : 
                 selectedElement.type === 'image' ? 'Image' : 'Vidéo'}
                {selectedElement.isDynamic && (
                  <span className="ml-1 text-orange-600">(Variable)</span>
                )}
              </span>
              <span className="text-gray-500">
                Position: ({Math.round(selectedElement.x)}, {Math.round(selectedElement.y)})
              </span>
              {selectedElement.type !== 'text' && (
                <span className="text-gray-500">
                  Taille: {Math.round(selectedElement.width)} × {Math.round(selectedElement.height)}px
                </span>
              )}
            </div>
            
            <div className="text-gray-500">
              Zoom: {Math.round(scale * 100)}% • Mode: {fitMode}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedCanvas;